import { Preload } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import {
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';
import { View } from '@cartwhl/3d/r3f/components/View';
import useGLTFWithCredentials from '@cartwhl/3d/r3f/hooks/useGLTFWithCredentials';
import { Variation } from '@cartwhl/common/types';
import Text from '@cartwhl/ui/base/Text';

import { JobOptionsDropdown } from 'components/JobOptionsDropdown';
import useLoadAnimation from 'hooks/useLoadAnimation';
import { sceneSettingsStore } from 'stores';

import ScissorView from './ScissorView';

export default function VariantionPicker({
  jobID,
  meshPath,
  variations,
  selectedVariation,
  setSelectedVariation,
  onFavoriteToggle,
}: {
  jobID: string;
  meshPath: string;
  variations: Record<string, Variation>;
  selectedVariation: number;
  setSelectedVariation: (variation: number) => void;
  onFavoriteToggle?: (animationNumber: string, favorite: boolean) => void;
}) {
  const { playbackSettings, backgroundSettings } = useSnapshot(
    sceneSettingsStore.state,
  );

  const items = Object.values(variations || {}).filter(
    (v) => v?.status === 'COMPLETED',
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const animations = useLoadAnimation(items.map((item) => item.bvhURL));

  const meshData = useGLTFWithCredentials(meshPath, undefined, [
    hideMeshMaterials,
    turnOnShadows,
    disableFrustumCulled,
  ]);

  return (
    <div className="relative h-[154px]" ref={containerRef}>
      <div className="styled-scrollbar flex h-full w-full gap-2 overflow-x-scroll p-1">
        {items.map((item, index) => (
          <div key={item.variationID} className="relative flex flex-col gap-2">
            <div className="bg-gray-15 dark:bg-gray-75 absolute top-1.5 left-1.5 z-1 flex h-5 w-5 items-center justify-center rounded-lg pt-px">
              <Text as="div" variant="studio/body/xs">
                {index + 1}
              </Text>
            </div>
            <button
              className={`bg-gray-30 relative block h-full w-[152px] flex-1 cursor-pointer overflow-hidden rounded-lg ${
                Number(item.variationID) === selectedVariation
                  ? 'shadow-outline-blue-3'
                  : 'hover:shadow-menu'
              }`}
              onClick={() => {
                setSelectedVariation(Number(item.variationID));
              }}
              style={{
                background: `linear-gradient(180deg, ${backgroundSettings.hexColor} -100%, #EFEFEF 85%)`,
              }}
            >
              <JobOptionsDropdown
                jobID={jobID}
                animationNumber={item.variationID}
                favorite={item.favorite}
                sentiment={item.feedback}
                mp4Path={item.mp4Path}
                prompt={item.prompt}
                onFavoriteToggle={onFavoriteToggle}
              />
              {meshData && animations?.[index]?.animationData && (
                <ScissorView
                  className="h-full w-full overflow-hidden"
                  meshData={meshData}
                  animationData={animations[index].animationData}
                  frameRate={playbackSettings.frameRate}
                  playbackSpeed={playbackSettings.playbackSpeed}
                  frameStepSize={playbackSettings.frameStepSize}
                  // mixerSync={undefined}
                />
              )}
            </button>
            <Text
              variant="studio/body/sm"
              className="text-gray-60 dark:text-gray-45"
            >
              Motion {index + 1}
            </Text>
          </div>
        ))}
      </div>

      <Canvas
        eventSource={containerRef.current || undefined}
        shadows
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          zIndex: 10000,
        }}
      >
        <View.Port />
        <Preload all />
      </Canvas>
    </div>
  );
}
